import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { finalize, Subject, takeUntil } from 'rxjs';
import { FormDto } from 'src/core/models/quality-management/form.dto';
import { FormVersionDto } from 'src/core/models/quality-management/form-version.dto';
import { FormVersionSelectionModel } from 'src/core/models/quality-management/form-version-selection.model';
import { FormVersionBlockViewModel } from '../../models/form-version-block-view.model';
import { FormVersionSelectorService } from '../../services/form-version-selector.service';

let nextInstanceId = 0;

@Component({
  selector: 'ca-multi-form-version-selector',
  templateUrl: './multi-form-version-selector.component.html',
  styleUrls: ['./multi-form-version-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MultiFormVersionSelectorComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => MultiFormVersionSelectorComponent),
    },
  ],
  standalone: false,
})
export class MultiFormVersionSelectorComponent
  implements OnChanges, OnDestroy, ControlValueAccessor, Validator
{
  @Input()
  forms: FormDto[] | null | undefined = null;

  blocks: FormVersionBlockViewModel[] = [];
  isDisabled: boolean = false;

  private selectedForms: FormDto[] = [];
  private readonly instanceId = ++nextInstanceId;
  private readonly states = new Map<number, FormVersionSelectionModel>();
  private readonly versionsCache = new Map<number, FormVersionDto[]>();
  private readonly loadingFormIds = new Set<number>();
  private readonly destroy$ = new Subject<void>();
  private isDestroyed = false;

  constructor(
    private service: FormVersionSelectorService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.forms) {
      this.applyForms();
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: FormVersionSelectionModel[] | null): void {
    this.states.clear();

    let isNormalized = false;

    (Array.isArray(value) ? value : []).forEach(selection => {
      if (selection?.formId == null) {
        return;
      }

      const writtenVersionId = selection.formVersion?.id ?? null;
      const isCachedSelection =
        this.versionsCache.has(selection.formId) && this.isFormSelected(selection.formId);

      if (selection.useAlwaysLatest !== false) {
        if (isCachedSelection && (this.getLatestVersion(selection.formId)?.id ?? null) !== writtenVersionId) {
          isNormalized = true;
        }

        return;
      }

      // Same fallback as the load path: a pinned version missing from the cached list becomes the latest.
      const formVersion =
        this.resolveVersion(selection.formId, selection.formVersion ?? null) ??
        this.getLatestVersion(selection.formId);

      this.states.set(selection.formId, { useAlwaysLatest: false, formVersion });

      if (isCachedSelection && (formVersion?.id ?? null) !== writtenVersionId) {
        isNormalized = true;
      }
    });

    this.rebuild();
    this.cdr.markForCheck();

    if (isNormalized) {
      // Deferred: writeValue can run before registerOnChange and inside the control's own setValue.
      Promise.resolve().then(() => {
        if (!this.isDestroyed) {
          this.emitValue();
        }
      });
    }
  }

  registerOnChange(fn: (value: FormVersionSelectionModel[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.rebuild();
    this.cdr.markForCheck();
  }

  // Sync on purpose: both filter panels gate on valid/invalid, and PENDING counts as neither.
  validate(): ValidationErrors | null {
    return this.isAnySelectedFormLoading() ? { formVersionsLoading: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onUseAlwaysLatestChange(formId: number, checked: boolean): void {
    const state = this.getState(formId);
    state.useAlwaysLatest = checked;

    // Unchecking preselects the latest version unless a previous pick is kept.
    if (!checked && !state.formVersion) {
      state.formVersion = this.getLatestVersion(formId);
    }

    this.rebuild();
    this.emitValue();
  }

  onVersionChange(formId: number, version: FormVersionDto | null): void {
    this.getState(formId).formVersion = version ?? null;
    this.rebuild();
    this.emitValue();
  }

  onClearSelectionClick(formId: number, eventArgs: MouseEvent): void {
    eventArgs.preventDefault();
    eventArgs.stopPropagation();

    this.states.delete(formId);
    this.rebuild();
    this.emitValue();
  }

  onDropdownOpen(versionSelect: NgSelectComponent): void {
    setTimeout(() => {
      const dropdownId = versionSelect?.dropdownId;
      const dropdownEl = dropdownId ? document.getElementById(dropdownId) : null;
      const selectEl = versionSelect?.element;

      if (!dropdownEl || !selectEl) {
        return;
      }

      const width = selectEl.getBoundingClientRect().width;

      dropdownEl.style.setProperty('width', `${width}px`, 'important');
      dropdownEl.style.setProperty('min-width', `${width}px`, 'important');
      dropdownEl.style.setProperty('max-width', `${width}px`, 'important');
    });
  }

  onChange(value: FormVersionSelectionModel[]): void {}

  onTouched(): void {}

  onValidatorChange(): void {}

  trackByFormId(index: number, block: FormVersionBlockViewModel): number {
    return block.formId;
  }

  compareVersions(item: FormVersionDto, selected: FormVersionDto): boolean {
    return item?.id === selected?.id;
  }

  get hasForms(): boolean {
    return this.blocks.length > 0;
  }

  private applyForms(): void {
    const previousIds = this.selectedForms.map(form => form.id);
    const wasLoading = this.isAnySelectedFormLoading();
    const seenIds = new Set<number>();

    this.selectedForms = (Array.isArray(this.forms) ? this.forms : []).filter(form => {
      if (form?.id == null || seenIds.has(form.id)) {
        return false;
      }

      seenIds.add(form.id);
      return true;
    });

    Array.from(this.states.keys())
      .filter(formId => !seenIds.has(formId))
      .forEach(formId => this.states.delete(formId));

    this.selectedForms.forEach(form => this.loadVersions(form.id));
    this.rebuild();

    const currentIds = this.selectedForms.map(form => form.id);
    const formsChanged =
      previousIds.length !== currentIds.length ||
      previousIds.some((formId, index) => formId !== currentIds[index]);

    const validatorOnly = !formsChanged && wasLoading !== this.isAnySelectedFormLoading();

    if (!formsChanged && !validatorOnly) {
      return;
    }

    // Deferred: ngOnChanges runs inside the parent's change detection.
    Promise.resolve().then(() => {
      if (this.isDestroyed) {
        return;
      }

      if (formsChanged) {
        this.emitValue();
      } else {
        // Same forms but a retry load started (e.g. after an HTTP error); no emit follows.
        this.onValidatorChange();
      }
    });
  }

  private loadVersions(formId: number): void {
    if (this.versionsCache.has(formId) || this.loadingFormIds.has(formId)) {
      return;
    }

    this.loadingFormIds.add(formId);

    this.service
      .getVersions(formId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          // Also reached via takeUntil on destroy, where the control must not be notified.
          if (this.loadingFormIds.delete(formId) && !this.isDestroyed) {
            this.rebuild();
            this.cdr.markForCheck();

            // Error path: emit the batched value if this was the last pending load, else only release validation.
            if (this.isFormSelected(formId)) {
              if (!this.isAnySelectedFormLoading()) {
                this.emitValue();
              } else {
                this.onValidatorChange();
              }
            }
          }
        }),
      )
      .subscribe(versions => {
        this.versionsCache.set(formId, versions ?? []);
        this.loadingFormIds.delete(formId);

        const state = this.states.get(formId);

        if (state) {
          state.formVersion = this.resolveVersion(formId, state.formVersion);

          if (!state.useAlwaysLatest && !state.formVersion) {
            state.formVersion = this.getLatestVersion(formId);
          }
        }

        this.rebuild();
        this.cdr.markForCheck();

        // Emit once after the last pending load; the error path in finalize covers a failing last load.
        if (this.isFormSelected(formId)) {
          if (this.isAnySelectedFormLoading()) {
            this.onValidatorChange();
          } else {
            this.emitValue();
          }
        }
      });
  }

  private rebuild(): void {
    const showFormName = this.selectedForms.length > 1;

    this.blocks = this.selectedForms.map((form, index) => {
      const state = this.states.get(form.id);
      const versions = this.versionsCache.get(form.id) ?? [];
      const loading = this.loadingFormIds.has(form.id);
      const useAlwaysLatest = state?.useAlwaysLatest ?? true;

      return {
        formId: form.id,
        formName: form.name,
        checkboxId: `useAlwaysLatestFormVersion_${this.instanceId}_${form.id}`,
        showFormName,
        showDivider: index > 0,
        useAlwaysLatest,
        selectedVersion: state?.formVersion ?? null,
        versions,
        latestVersionNumber: versions[0]?.version ?? null,
        loading,
        checkboxDisabled: this.isDisabled || loading || versions.length === 0,
        dropdownDisabled: this.isDisabled || loading,
      };
    });
  }

  private emitValue(): void {
    this.onChange(
      this.blocks.map(block => ({
        formId: block.formId,
        useAlwaysLatest: block.useAlwaysLatest,
        formVersion: block.useAlwaysLatest ? (block.versions[0] ?? null) : block.selectedVersion,
      })),
    );
  }

  private getState(formId: number): FormVersionSelectionModel {
    let state = this.states.get(formId);

    if (!state) {
      state = { useAlwaysLatest: true, formVersion: null };
      this.states.set(formId, state);
    }

    return state;
  }

  // Swap in the cached instance so ng-select and consumers see the loaded DTO.
  private resolveVersion(formId: number, version: FormVersionDto | null): FormVersionDto | null {
    const versions = this.versionsCache.get(formId);

    if (!version || !versions) {
      return version;
    }

    return versions.find(item => item.id === version.id) ?? null;
  }

  private getLatestVersion(formId: number): FormVersionDto | null {
    return this.versionsCache.get(formId)?.[0] ?? null;
  }

  private isFormSelected(formId: number): boolean {
    return this.selectedForms.some(form => form.id === formId);
  }

  private isAnySelectedFormLoading(): boolean {
    return this.selectedForms.some(form => this.loadingFormIds.has(form.id));
  }
}
